import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { ProfileSection } from "@/lib/api/profile-sections";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Edit, GripVertical, Trash2 } from "lucide-react";
import { useState } from "react";

interface ProfileSectionItemProps {
  section: ProfileSection;
  onEdit: (section: ProfileSection) => void;
  onDelete: (sectionId: string) => void;
  isDeleting?: boolean;
}

export function ProfileSectionItem({
  section,
  onEdit,
  onDelete,
  isDeleting = false,
}: ProfileSectionItemProps) {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { url: signedImageUrl } = useSignedMedia(section.image_url);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: section.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleDelete = () => {
    onDelete(section.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
      <Card
        ref={setNodeRef}
        style={style}
        className={`p-4 ${isDragging ? "shadow-lg" : ""}`}
      >
        <div className="flex gap-3">
          {/* Drag handle */}
          <div
            className="flex cursor-grab items-start pt-1 active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="text-muted-foreground h-5 w-5" />
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-start justify-between gap-2">
              <h3 className="text-lg font-semibold leading-tight">
                {section.title}
              </h3>
              <div className="flex flex-shrink-0 gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(section)}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={isDeleting}
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Image */}
            {signedImageUrl && (
              <div className="mb-3">
                <img
                  src={signedImageUrl}
                  alt={section.title}
                  className="h-48 w-full max-w-md rounded-lg border object-cover"
                />
              </div>
            )}

            {/* Description */}
            <div className="text-muted-foreground text-sm">
              <p className={showFullDescription ? "" : "line-clamp-3"}>
                {section.description}
              </p>
              {section.description.length > 200 && (
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="mt-1 text-xs text-primary hover:underline"
                >
                  {showFullDescription ? "Show less" : "Show more"}
                </button>
              )}
            </div>

            {/* Metadata */}
            <div className="text-muted-foreground mt-3 flex items-center gap-4 text-xs">
              <span>Position: {section.position + 1}</span>
              <span>
                Created: {new Date(section.created_at).toLocaleDateString()}
              </span>
              {section.updated_at !== section.created_at && (
                <span>
                  Updated: {new Date(section.updated_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      <ConfirmActionDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        title="Delete Profile Section"
        description={`Are you sure you want to delete "${section.title}"? This action cannot be undone.`}
      />
    </>
  );
}
